/**
 * AI 队友页（demo 旗舰功能）：和组队顾问「小叉」多轮对话。
 *
 * - 气泡对话 + typing 指示 + 建议话题（借鉴 v4.1 参考稿的交互范式）；
 * - 每条 AI 回答标注 via（LLM 生成 / 规则回退）与 contextUsed 上下文 badge：
 *   评委一眼能看到"它回答的是我这个队的真实数据"；
 * - 历史保存在组件状态（刷新清空），与服务端无状态设计对应。
 */

import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';

import { assistantApi } from '../api/assistant.js';
import type { AssistantChatDto } from '../api/types.js';
import { Button } from '../components/Button.js';
import { Tag } from '../components/Tag.js';

interface ChatEntry extends AssistantChatDto {
  id: number;
  question: string;
}

const SUGGESTIONS = ['帮我从候选池挑几个队友', '最近有什么 ddl？', '我的队伍现在什么情况？', '我们队还缺什么角色？'];

let nextId = 1;

export function AssistantPage() {
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: (messages: { role: 'user' | 'assistant'; content: string }[]) => assistantApi.chat({ messages }),
  });

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [entries, send.isPending]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const question = input.trim();
    if (question === '' || send.isPending) return;
    setInput('');
    const history: { role: 'user' | 'assistant'; content: string }[] = [
      ...entries.flatMap((entry) => [
        { role: 'user' as const, content: entry.question },
        { role: 'assistant' as const, content: entry.reply },
      ]),
      { role: 'user' as const, content: question },
    ];
    const id = nextId++;
    send.mutate(history, {
      onSuccess: (data) => setEntries((prev) => [...prev, { ...data, id, question }]),
      // 失败不污染对话流：全局 MutationCache 会统一弹错误提示，这里把问题放回输入框方便重发
      onError: () => setInput(question),
    });
  };

  const lastEntry = entries.length > 0 ? (entries[entries.length - 1] as ChatEntry) : null;
  const lastVia = lastEntry !== null ? lastEntry.via : null;

  return (
    <div className="stack assistant-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">
            AI 队友 <span aria-hidden="true">✨</span>
          </h1>
          <p className="page-sub">
            组队顾问「小叉」：基于你<strong>真实的</strong>队伍、候选池与赛事数据回答——说人话，给建议，不编数字。
          </p>
        </div>
        {lastVia !== null ? (
          lastVia === 'llm' ? (
            <Tag tone="primary" title="由后端真实调用大模型生成">
              LLM 生成
            </Tag>
          ) : (
            <Tag tone="muted" title="未配置 LLM Key 或调用失败，由规则应答（同样基于真实数据）">
              规则应答
            </Tag>
          )
        ) : null}
      </div>

      <div className="chat-panel">
        <div className="chat-scroll" ref={listRef} aria-live="polite">
          {entries.length === 0 ? (
            <div className="chat-welcome">
              <div className="chat-welcome-avatar" aria-hidden="true">
                ✨
              </div>
              <p>
                你好，我是小叉。我可以帮你<strong>挑队友</strong>（从候选池里按技能找人）、<strong>盯节点</strong>
                （赛事倒计时与备赛节奏）、<strong>看队伍</strong>（分工与进度建议）。试试下面的话题，或直接输入你的问题。
              </p>
            </div>
          ) : null}

          {entries.map((entry) => (
            <div key={entry.id} className="chat-exchange">
              <div className="chat-bubble user">
                <span>{entry.question}</span>
              </div>
              <div className="chat-bubble ai">
                {entry.reply}
                {entry.contextUsed.length > 0 ? (
                  <span className="chat-context">
                    {entry.contextUsed.map((source) => (
                      <span key={source} className="context-badge">
                        {source}
                      </span>
                    ))}
                  </span>
                ) : null}
              </div>
            </div>
          ))}

          {send.isPending ? (
            <div className="chat-bubble ai typing" aria-label="小叉正在输入">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          ) : null}
        </div>

        {entries.length === 0 ? (
          <div className="chat-suggestions">
            {SUGGESTIONS.map((suggestion) => (
              <button key={suggestion} type="button" className="chip" onClick={() => setInput(suggestion)}>
                {suggestion}
              </button>
            ))}
          </div>
        ) : null}

        <form className="chat-input-row" onSubmit={handleSubmit}>
          <input
            className="input"
            value={input}
            placeholder="向小叉提问…（如：帮我找会设计的同学）"
            maxLength={500}
            onChange={(event) => setInput(event.target.value)}
            aria-label="向 AI 队友提问"
          />
          <Button type="submit" variant="primary" loading={send.isPending} disabled={input.trim() === ''}>
            发送
          </Button>
          {entries.length > 0 ? (
            <Button variant="ghost" onClick={() => setEntries([])} disabled={send.isPending}>
              清空
            </Button>
          ) : null}
        </form>
      </div>

      <p className="chart-note">
        小叉的回答基于你账号下的真实数据（队伍/候选池/赛事里程碑），服务端不保存对话记录；未配置大模型 Key 时自动切换规则应答，功能不中断。
      </p>
    </div>
  );
}
